import { formatMoney } from '../../../../utils/common'
import { visibilityVisitTypeValues } from '../../Constants'
import { useService } from '../../hooks/useService'
import { getVisitMethod } from '../../visitMethods'

type VisibilityKey = keyof typeof visibilityVisitTypeValues

function isVisibilityKey(value: string): value is VisibilityKey {
  return value in visibilityVisitTypeValues
}

type Props = {
  currentVisitTypeId: string
}

export const VisitTypeViewMode = ({ currentVisitTypeId }: Props) => {
  const params = new URLSearchParams(window.location.search)
  const id = params.get('id')

  const { savedData } = useService(id)
  const currentServiceType = id !== null ? savedData[id] : undefined
  const currentVisitType = currentServiceType?.VisitTypes?.find(({ VisitTypeId }) => VisitTypeId === currentVisitTypeId)

  if (!currentVisitType) return null

  const visitMethod = getVisitMethod(currentVisitType)
  const visibilityType = currentVisitType.VisibilityType
  const visibilityLabel =
    visibilityType !== null && visibilityType !== undefined && isVisibilityKey(visibilityType) ?
      visibilityVisitTypeValues[visibilityType]
    : (visibilityType ?? '')
  const intakeFormName = currentVisitType.IntakeForm?.Name ?? '--'
  const consentFormNames =
    currentVisitType.ConsentForms?.map((f) => f.Name)
      .filter((name): name is string => name !== null && name !== undefined)
      .join(', ') || '--'

  return (
    /*
     * SCSS → Tailwind mapping notes:
     * .vtv-card: bg-white border border-shade-10 rounded-large overflow-hidden
     * .vtv-card-header: flex items-center flex-wrap gap-sp-1.5 py-sp-2.5 px-sp-3
     * .vtv-visit-name: text-size-5 font-semibold leading-7 text-text-dark
     * .vtv-pills: flex flex-wrap gap-sp-0.75
     * .vtv-pill: text-size-2 font-medium leading-4 text-text-dark bg-deep-navy-5 border border-shade-10
     *             rounded-full py-[3px] px-[10px] whitespace-nowrap
     *             (3px and 10px have no exact DS spacing tokens: sp-0.25=2px, sp-0.5=4px, sp-1=8px, sp-1.5=12px)
     * .vtv-divider: h-px bg-shade-10
     * .vtv-section: py-sp-2.5 px-sp-3 flex flex-col gap-sp-0.75
     * .vtv-section-label: text-size-2 font-semibold leading-4 text-text-light
     *   (SCSS rgba(37,56,69,0.7) ≈ #253845B2 → text-text-light)
     * .vtv-section-value: text-size-3 font-normal leading-5 text-text-dark
     * .vtv-details-grid: grid grid-cols-2 py-sp-2.5 px-sp-3
     * .vtv-details-col: flex flex-col gap-sp-2
     * .vtv-details-title: text-size-2 font-semibold leading-4 text-text-light mb-sp-0.5
     * .vtv-detail-row: flex flex-col gap-[2px]  (2px = sp-0.25)
     * .vtv-detail-label: text-size-2 font-normal leading-4 text-text-light
     * .vtv-detail-value: text-size-3 font-semibold leading-5 text-text-dark
     * .vtv-forms: gap-sp-1.5 (overrides vtv-section's gap-sp-0.75)
     */
    <div className='overflow-hidden rounded-large border border-shade-10 bg-white'>
      <div className='flex flex-wrap items-center gap-sp-1.5 px-sp-3 py-sp-2.5'>
        <span className='text-size-5 font-semibold leading-7 text-text-dark'>{currentVisitType.Name}</span>
        <div className='flex flex-wrap gap-sp-0.75'>
          <span className='whitespace-nowrap rounded-full border border-shade-10 bg-deep-navy-5 px-[10px] py-[3px] text-size-2 font-medium leading-4 text-text-dark'>
            {visibilityLabel}
          </span>
          <span className='whitespace-nowrap rounded-full border border-shade-10 bg-deep-navy-5 px-[10px] py-[3px] text-size-2 font-medium leading-4 text-text-dark'>
            {visitMethod}
          </span>
          <span className='whitespace-nowrap rounded-full border border-shade-10 bg-deep-navy-5 px-[10px] py-[3px] text-size-2 font-medium leading-4 text-text-dark'>
            {currentVisitType.Duration} min
          </span>
          <span className='whitespace-nowrap rounded-full border border-shade-10 bg-deep-navy-5 px-[10px] py-[3px] text-size-2 font-medium leading-4 text-text-dark'>
            ${formatMoney(currentVisitType.Cost)}
          </span>
        </div>
      </div>
      <div className='h-px bg-shade-10' />
      <div className='flex flex-col gap-sp-0.75 px-sp-3 py-sp-2.5'>
        <span className='text-size-2 font-semibold leading-4 text-text-light'>Description</span>
        <span className='text-size-3 font-normal leading-5 text-text-dark'>{currentVisitType.Description ?? '--'}</span>
      </div>
      <div className='h-px bg-shade-10' />
      <div className='grid grid-cols-2 px-sp-3 py-sp-2.5'>
        <div className='flex flex-col gap-sp-2'>
          <span className='mb-sp-0.5 text-size-2 font-semibold leading-4 text-text-light'>Payment</span>
          <div className='flex flex-col gap-sp-0.25'>
            <span className='text-size-2 font-normal leading-4 text-text-light'>Cancellation fee</span>
            <span className='text-size-3 font-semibold leading-5 text-text-dark'>
              ${formatMoney(currentVisitType.CancellationFee)}
            </span>
          </div>
        </div>
        <div className='flex flex-col gap-sp-2'>
          <span className='mb-sp-0.5 text-size-2 font-semibold leading-4 text-text-light'>Rules</span>
          <div className='flex flex-col gap-sp-0.25'>
            <span className='text-size-2 font-normal leading-4 text-text-light'>Drug screening</span>
            <span className='text-size-3 font-semibold leading-5 text-text-dark'>
              {currentVisitType.IsMedicationAssistedTreatment ? 'Yes' : 'No'}
            </span>
          </div>
          <div className='flex flex-col gap-sp-0.25'>
            <span className='text-size-2 font-normal leading-4 text-text-light'>Booking reminders</span>
            <span className='text-size-3 font-semibold leading-5 text-text-dark'>
              {currentVisitType.IsPostBookingReminderRequired ? 'Yes' : 'No'}
            </span>
          </div>
          <div className='flex flex-col gap-sp-0.25'>
            <span className='text-size-2 font-normal leading-4 text-text-light'>New patient spacing</span>
            <span className='text-size-3 font-semibold leading-5 text-text-dark'>
              {currentVisitType.IsNewPatientSpacingEnabled ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>
      <div className='h-px bg-shade-10' />
      <div className='flex flex-col gap-sp-1.5 px-sp-3 py-sp-2.5'>
        <span className='text-size-2 font-semibold leading-4 text-text-light'>Forms</span>
        <div className='flex flex-col gap-sp-0.25'>
          <span className='text-size-2 font-normal leading-4 text-text-light'>Intake forms</span>
          <span className='text-size-3 font-semibold leading-5 text-text-dark'>{intakeFormName}</span>
        </div>
        <div className='flex flex-col gap-sp-0.25'>
          <span className='text-size-2 font-normal leading-4 text-text-light'>Consent forms</span>
          <span className='text-size-3 font-semibold leading-5 text-text-dark'>{consentFormNames}</span>
        </div>
      </div>
    </div>
  )
}
